/**
 * Product tab — goal lifecycle funnel, recalibration triggers, intelligence
 * engine usage, feature adoption.
 */
import React from 'react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { type AnalyticsRange } from '../hooks/useAnalyticsRange';
import type {
  FeatureAdoptionPayload,
  GoalLifecyclePayload,
  IntelligenceEnginesPayload,
  RecalibrationsProductPayload,
} from '../types';
import { RefreshCw } from 'lucide-react';
import { ChartCard } from '../components/ChartCard';
import { TrendChart } from '../components/TrendChart';
import { KpiTile } from '../components/KpiTile';
import { FeatureAdoptionTable } from '../components/FeatureAdoptionTable';
import { AcquisitionFunnel } from '../components/AcquisitionFunnel';
import { CATEGORICAL, CEDAR } from '../theme';
import { formatCompact, formatCurrency, formatFloat, formatInt } from '../format';

export interface ProductTabProps {
  range: AnalyticsRange;
}

export const ProductTab: React.FC<ProductTabProps> = ({ range }) => {
  const goals = useAnalyticsQuery<GoalLifecyclePayload>(
    ['goal-lifecycle', range.queryString],
    `/api/admin/analytics/product/goal-lifecycle?${range.queryString}`,
  );
  const recalibrations = useAnalyticsQuery<RecalibrationsProductPayload>(
    ['recalibrations-product', range.queryString],
    `/api/admin/analytics/product/recalibrations?${range.queryString}`,
  );
  const engines = useAnalyticsQuery<IntelligenceEnginesPayload>(
    ['intelligence-engines', range.queryString],
    `/api/admin/analytics/product/intelligence-engines?${range.queryString}`,
  );
  const adoption = useAnalyticsQuery<FeatureAdoptionPayload>(
    ['feature-adoption', range.queryString],
    `/api/admin/analytics/product/feature-adoption?${range.queryString}`,
  );

  // Transform the goal stages into an acquisition-funnel-compatible shape.
  const goalStages = React.useMemo(() => {
    const stages = goals.data?.data.stages ?? [];
    const base = stages.find((s) => s.stage === 'discovery')?.count ?? 0;
    return stages.map((s, i) => ({
      stage: s.stage,
      count: s.count,
      pct_of_signup: base > 0 ? (s.count / base) * 100 : 0,
      pct_of_prior: i === 0 ? 100 : (s.count / (stages[i - 1]?.count || 1)) * 100,
    }));
  }, [goals.data]);

  return (
    <div className="space-y-6">
      {/* Goal lifecycle funnel */}
      <ChartCard
        title="Goal lifecycle"
        subtitle="Discovery → refinement → confirmed → plan → milestone"
        loading={goals.isLoading}
        error={goals.error?.message ?? null}
        isEmpty={!goalStages.length}
        onRetry={() => goals.refetch()}
      >
        <AcquisitionFunnel stages={goalStages} />
      </ChartCard>

      {/* Recalibration headline stat — avg per user per week (definition on hover) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div title={recalibrations.data?.data.avg_per_user_per_week_definition ?? undefined}>
          <KpiTile
            label="Recals / user / week"
            value={formatFloat(recalibrations.data?.data.avg_per_user_per_week, 2)}
            hint={recalibrations.data?.data.avg_per_user_per_week_definition}
            icon={<RefreshCw className="h-3 w-3" />}
            loading={recalibrations.isLoading}
          />
        </div>
      </div>

      {/* Recalibration time series + trigger breakdown */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ChartCard
          title="Recalibrations over time"
          subtitle="Plan adjustments — the life-happens signal"
          loading={recalibrations.isLoading}
          error={recalibrations.error?.message ?? null}
          isEmpty={!recalibrations.data?.data.series?.length}
          onRetry={() => recalibrations.refetch()}
          className="lg:col-span-2"
        >
          <TrendChart
            data={recalibrations.data?.data.series ?? []}
            xKey="day"
            type="area"
            height={240}
            series={[{ key: 'count', label: 'Recalibrations' }]}
            xFormat={formatDayShort}
            showLegend={false}
          />
        </ChartCard>

        <ChartCard
          title="Triggers"
          subtitle="What caused recalibrations"
          loading={recalibrations.isLoading}
          error={recalibrations.error?.message ?? null}
          isEmpty={!recalibrations.data?.data.triggers?.length}
          onRetry={() => recalibrations.refetch()}
          className="lg:col-span-1"
        >
          <ul className="space-y-2">
            {(recalibrations.data?.data.triggers ?? []).map((t, i) => {
              const max = recalibrations.data?.data.triggers[0]?.count ?? 1;
              const pct = max > 0 ? (t.count / max) * 100 : 0;
              return (
                <li key={t.trigger}>
                  <div className="flex items-baseline justify-between text-[11px]">
                    <span className="font-mono text-gray-700">{t.trigger}</span>
                    <span className="tabular-nums text-gray-800">{formatInt(t.count)}</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: CATEGORICAL[i % CATEGORICAL.length],
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </ChartCard>
      </div>

      {/* Intelligence engines */}
      <ChartCard
        title="Intelligence engine usage"
        subtitle="Invocations and cost for the 8 intelligence engines only. (Not an error/regression: the earlier 'uncategorized' spike was the chart aggregating the ENTIRE LLM ledger — main chat, extraction, goals, etc. — not just the engines. Those non-engine calls are now excluded.)"
        loading={engines.isLoading}
        error={engines.error?.message ?? null}
        isEmpty={!engines.data?.data.engines?.length}
        onRetry={() => engines.refetch()}
      >
        <EngineUsagePanel rows={engines.data?.data.engines ?? []} />
      </ChartCard>

      {/* Feature adoption */}
      <ChartCard
        title="Feature adoption"
        subtitle="% of active users (opened the app) using each feature in the selected range. Adoption %s stepped down when the opened-app signal widened the denominator."
        loading={adoption.isLoading}
        error={adoption.error?.message ?? null}
        isEmpty={!adoption.data?.data.features?.length}
        onRetry={() => adoption.refetch()}
      >
        <FeatureAdoptionTable
          features={adoption.data?.data.features ?? []}
          mau={adoption.data?.data.mau ?? 0}
        />
      </ChartCard>
    </div>
  );
};

const ENGINE_LABELS: Record<string, string> = {
  athletic: 'Athletic',
  nutrition: 'Nutrition',
  sleep: 'Sleep',
  movement: 'Movement',
  equipment: 'Equipment',
  womens_health: "Women's Health",
  medical: 'Medical Safety',
  physio: 'Physiotherapy',
};

const EngineUsagePanel: React.FC<{
  rows: IntelligenceEnginesPayload['engines'];
}> = ({ rows }) => {
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.invocations), 1);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {rows.map((r) => {
        const pct = (r.invocations / max) * 100;
        return (
          <div
            key={r.engine}
            className="rounded-md border border-gray-100 bg-gray-50/40 p-3"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {ENGINE_LABELS[r.engine] ?? r.engine}
              </span>
              <span className="text-[11px] font-semibold tabular-nums text-gray-700">
                {formatInt(r.invocations)} invocations
              </span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white">
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, backgroundColor: CEDAR.stream }}
              />
            </div>
            <div className="mt-1 flex justify-between text-[10.5px] text-gray-500">
              <span>{formatInt(r.unique_users)} users</span>
              <span>{formatCurrency(r.cost, 2)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

function formatDayShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
